'use client';

import { motion } from 'framer-motion';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/app/utils';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * 搭配 HomeSelectHighlight 使用的选项样式：
 *
 * - 选中态背景交给滑块（这里把 focus / checked 的自带背景清成透明），滑块在 z-0，选项在 z-1；
 * - hover 态用 before 伪元素铺白 8% 底色，尺寸和滑块一致（都覆盖整个选项 padding-box）；
 * - 选中项自身不显示 hover（data-[state=checked] 时伪元素背景强制透明），避免叠加。
 *
 * 不直接写 data-[highlighted]:bg-* 是因为 base SelectItem 已有 focus:bg-system-hover01，
 * 而 Radix 在鼠标 hover 时会真的调 DOM focus()，两条规则会抢同一个 background，谁生效
 * 取决于 Tailwind 变体生成顺序（不可靠）。改用伪元素后两者各占一层，不存在覆盖竞争。
 */
export const HOME_SELECT_ITEM_CLASS_NAME =
  'relative z-[1] rounded-8 py-3 text-system-white focus:bg-transparent data-[state=checked]:bg-transparent before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-transparent before:transition-colors data-[highlighted]:before:bg-white/[0.08] data-[state=checked]:before:!bg-transparent';

/**
 * Select 选项列表里的「选中态」滑块：跟踪 containerRef 内 data-state="checked" 的选项，
 * 量出位置后用 framer-motion 滑过去，取代 Radix 自带的瞬切背景色。
 *
 * 只管选中态，不管 hover——hover 态由选项自身的 data-[highlighted]:bg-* 纯 CSS 处理，
 * 两层叠加：常驻的选中块 + 跟着鼠标即时响应的 hover 底色。
 *
 * 必须搭配 useDelayedSelectClose：Radix 默认选中即关闭面板，滑动动画根本没有时间播放。
 */
export const HomeSelectHighlight = ({
  containerRef,
  activeValue,
  className,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  activeValue: string;
  /** 覆盖圆角等外观，需与对应 SelectItem 的圆角保持一致，否则滑块和选项形状会错位。 */
  className?: string;
}) => {
  const [rect, setRect] = useState<HighlightRect | null>(null);
  const lastValidRect = useRef<HighlightRect | null>(null);

  /**
   * Radix Select 面板关闭时会把这个组件渡入一个脱离文档流的 DocumentFragment，
   * containerRef.current 在那个阶段指向的是一个尚未挂载到真实 document 的节点；
   * 面板打开后切到真实渲染分支，ref 才指向可用的 DOM——但这个组件实例本身不会
   * 随 open 状态重新 mount，所以不能假设 ref 在 effect 首次运行时就已经可用。
   *
   * 用 MutationObserver 监听 document.body 子树：一旦 containerRef.current 挂载
   * 到真实文档（Radix Portal 挂载或 data-highlighted/data-state 变化），立即测量。
   * 只在浏览器 idle 时被动触发，不依赖任何一次性的挂载时机猜测。
   */
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const checked = container?.isConnected ? container.querySelector<HTMLElement>('[data-state="checked"]') : null;

      if (!container || !checked) {
        setRect(null);
        return;
      }

      const containerBox = container.getBoundingClientRect();
      const itemBox = checked.getBoundingClientRect();

      // 脱离文档流或尚未布局的元素 getBoundingClientRect 会返回全 0 矩形，过滤掉。
      if (itemBox.width === 0 || itemBox.height === 0) {
        setRect(null);
        return;
      }

      const next: HighlightRect = {
        top: itemBox.top - containerBox.top,
        left: itemBox.left - containerBox.left,
        width: itemBox.width,
        height: itemBox.height,
      };

      // 防止后续测量拿到缩水值（如在动画中途或滑块遮挡时测量）覆盖正确尺寸。
      // 只在以下情况更新：首次测量、尺寸变大、或位置变了（但尺寸不能缩小）。
      const prev = lastValidRect.current;
      const shouldUpdate =
        !prev ||
        next.top !== prev.top ||
        next.left !== prev.left ||
        next.width > prev.width ||
        next.height > prev.height;

      if (shouldUpdate && next.width >= (prev?.width ?? 0) && next.height >= (prev?.height ?? 0)) {
        lastValidRect.current = next;
        setRect(next);
      }
    };

    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true,
      childList: true,
    });

    measure();

    return () => observer.disconnect();
  }, [containerRef, activeValue]);

  if (!rect) {
    return null;
  }

  return (
    <motion.div
      // 与宽高比等设置项的选中态一致：白 18% 描边 + 白 14% 底色。
      // 用 box-shadow 模拟 border，避免 border 占 box model 空间导致尺寸和选项对不齐。
      // box-border 确保 animate 的 width/height 是最终渲染尺寸（不被 padding/border 挤占）。
      className={cn(
        'pointer-events-none absolute box-border rounded-6 bg-white/[0.14] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]',
        className,
      )}
      initial={false}
      animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      style={{ zIndex: 0 }}
    />
  );
};
